import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy,
  Download,
  Share2,
  Mail,
  MessageSquare,
  Printer,
  ArrowLeft,
  Zap,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import ActivationModal from "@/components/ActivationModal";
import SendQRModal from "@/components/SendQRModal";

export default function QRDetail() {
  const { vid } = useParams();
  const nav = useNavigate();
  const { activeProperty, user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [vehicle, setVehicle] = useState(null);
  const [qr, setQr] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [modal, setModal] = useState(null);
  const [sendChannel, setSendChannel] = useState(null);

  useEffect(() => {
    api
      .get(`/vehicles/${vid}`)
      .then((r) => {
        setVehicle(r.data);
        if (r.data?.tenant_id) {
          api.get(`/tenants/${r.data.tenant_id}`).then((rt) => setTenant(rt.data)).catch(() => {});
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
    api.get(`/vehicles/${vid}/qr`).then((r) => setQr(r.data)).catch(() => {});
  }, [vid]);

  const copyLink = () => {
    navigator.clipboard.writeText(qr.scan_url);
    toast.success("Link copied");
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${qr.png_base64}`;
    a.download = `scanmint-${qr.qr_code}.png`;
    a.click();
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "ScanMint QR",
          text: "Verify this vehicle",
          url: qr.scan_url,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  const email = () => setSendChannel("email");
  const text = () => setSendChannel("sms");

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>Print QR</title></head>
      <body style="font-family: system-ui; padding:40px; text-align:center">
        <img src="data:image/png;base64,${qr.png_base64}" style="width:320px;height:320px"/>
        <h2 style="margin-top:20px">${vehicle.year} ${vehicle.make} ${vehicle.model}</h2>
        <p style="color:#71717A">Plate ${vehicle.license_plate} · ${vehicle.state}</p>
        <p style="font-family: monospace; color:#71717A">${qr.qr_code}</p>
        <script>window.print()</script>
      </body></html>
    `);
    w.document.close();
  };

  if (!vehicle || !qr) {
    return <div className="text-sm text-[#71717A]">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => nav(-1)} className="mb-2" data-testid="qr-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E4E4E7] rounded-lg p-8 flex flex-col items-center">
          <div className="mono-tiny text-[#71717A] mb-4">QR · {qr.qr_code}</div>
          <img
            src={`data:image/png;base64,${qr.png_base64}`}
            alt="QR"
            className="w-64 h-64"
            data-testid="qr-image"
          />
          <div className="mt-5 text-center">
            <div className="font-display text-xl font-bold">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </div>
            <div className="mono-tiny text-[#71717A] mt-1">
              {vehicle.license_plate} · {vehicle.state}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <div className="mono-tiny text-[#71717A]">Status</div>
            <div className="flex items-center justify-between mt-2">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${
                  vehicle.status === "active"
                    ? "bg-[#00C16E]/10 text-[#008A4F] border border-[#00C16E]/30"
                    : "bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7]"
                }`}
                data-testid="qr-status"
              >
                {vehicle.status}
              </span>
              {vehicle.status !== "active" && isSuperAdmin && (
                <Button
                  onClick={() => setModal(vehicle)}
                  className="h-10 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="qr-activate"
                >
                  <Zap className="w-4 h-4 mr-2" /> Activate
                </Button>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <div className="mono-tiny text-[#71717A] mb-3">Public scan URL</div>
            <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2 text-sm font-mono break-all">
              {qr.scan_url}
            </div>

            <div className="grid grid-cols-3 gap-2 mt-5">
              <Action icon={Copy} label="Copy" onClick={copyLink} tid="qr-copy" />
              <Action icon={Download} label="Download" onClick={download} tid="qr-download" />
              <Action icon={Share2} label="Share" onClick={share} tid="qr-share" />
              <Action icon={Mail} label="Email" onClick={email} tid="qr-email" />
              <Action icon={MessageSquare} label="Text" onClick={text} tid="qr-text" />
              <Action icon={Printer} label="Print" onClick={print} tid="qr-print" />
            </div>
          </div>
        </div>
      </div>

      {modal && (
        <ActivationModal
          vehicle={modal}
          property={activeProperty}
          onClose={() => setModal(null)}
          onActivated={() => {
            api.get(`/vehicles/${vid}`).then((r) => setVehicle(r.data));
          }}
        />
      )}
      <SendQRModal
        open={!!sendChannel}
        onClose={() => setSendChannel(null)}
        vehicle={vehicle}
        defaultEmail={tenant?.email || ""}
        defaultPhone={tenant?.phone || ""}
      />
    </div>
  );
}

function Action({ icon: Icon, label, onClick, tid }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className="h-12 flex flex-col gap-1 border-[#E4E4E7]"
      data-testid={tid}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-[11px]">{label}</span>
    </Button>
  );
}
